import { dictionaryApi } from './dictionaryClient';

export type SutraCommentary = {
  id: number;
  name: string;
};

function isRecord(x: unknown): x is Record<string, unknown> {
  return x != null && typeof x === 'object' && !Array.isArray(x);
}

function normalizeOne(raw: unknown): SutraCommentary | null {
  if (!isRecord(raw)) return null;
  const idRaw = raw.id;
  const id = typeof idRaw === 'number' ? idRaw : Number(idRaw);
  if (!Number.isFinite(id)) return null;
  const name = raw.name == null ? '' : String(raw.name);
  return { id, name };
}

function normalizeList(data: unknown): SutraCommentary[] {
  const rows =
    Array.isArray(data) ? data : isRecord(data) && Array.isArray(data.data) ? (data.data as unknown[]) : [];
  return rows.map(normalizeOne).filter((x): x is SutraCommentary => x != null);
}

function unwrapEntityPayload(raw: unknown): unknown {
  if (isRecord(raw) && 'data' in raw) return raw.data;
  return raw;
}

export async function listSutraCommentary(): Promise<SutraCommentary[]> {
  const res = await dictionaryApi.get<unknown>('/sutra-commentary');
  return normalizeList(res.data);
}

export async function getSutraCommentary(id: number): Promise<SutraCommentary> {
  const res = await dictionaryApi.get<unknown>(`/sutra-commentary/${id}`);
  const row = normalizeOne(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid sutra commentary response');
  return row;
}

export async function createSutraCommentary(body: { name: string }): Promise<SutraCommentary> {
  const res = await dictionaryApi.post<unknown>('/sutra-commentary', body);
  const row = normalizeOne(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid sutra commentary response');
  return row;
}

export async function updateSutraCommentary(
  id: number,
  body: Partial<Pick<SutraCommentary, 'name'>>
): Promise<SutraCommentary> {
  const res = await dictionaryApi.patch<unknown>(`/sutra-commentary/${id}`, body);
  const row = normalizeOne(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid sutra commentary response');
  return row;
}

export async function deleteSutraCommentary(id: number): Promise<void> {
  await dictionaryApi.delete(`/sutra-commentary/${id}`);
}
